import type { Point } from "~/lib/svgEditor/zoom";

/** A command of path data: absolute, as the path is drawn. */
export type PathCommand =
    | {
          kind: "A";
          large: boolean;
          /** The ellipse's x axis, turned by this many degrees. */
          rotation: number;
          rx: number;
          ry: number;
          sweep: boolean;
          x: number;
          y: number;
      }
    | {
          kind: "C";
          x: number;
          x1: number;
          x2: number;
          y: number;
          y1: number;
          y2: number;
      }
    | { kind: "L" | "M"; x: number; y: number }
    | { kind: "Q"; x: number; x1: number; y: number; y1: number }
    | { kind: "Z" };

/** An attribute of a figure, as the DOM gives it: null or undefined without. */
type Attribute = (name: string) => string | null | undefined;

/** The last control point, for a smooth curve of the same family to mirror. */
type Control = { family: "C" | "Q"; point: Point };

/** Where reading path data stands. */
type Reading = {
    control: Control | undefined;
    /** Whoever reads the commands has what they wanted: reading ends. */
    done: boolean;
    point: Point;
    start: Point;
    /** Gets each command; true when no further ones are wanted. */
    visit: (command: PathCommand) => boolean | undefined;
};

/** How many numbers each path command takes. */
const ARITY = new Map<string, number>([
    ["a", 7],
    ["c", 6],
    ["h", 1],
    ["l", 2],
    ["m", 2],
    ["q", 4],
    ["s", 4],
    ["t", 2],
    ["v", 1],
    ["z", 0],
]);

const NUMBERS = /[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/gi;
// The data is read character by character, in place: a pattern for every
// number, or cutting the data down after it, takes several times as long
// over the hundreds of thousands of numbers of a large selection's paths.
const PLUS = 0x2b;
const COMMA = 0x2c;
const MINUS = 0x2d;
const DOT = 0x2e;
const ZERO = 0x30;
const NINE = 0x39;
const UPPER_A = 0x41;
const UPPER_E = 0x45;
const UPPER_Z = 0x5a;
const LOWER_A = 0x61;
const LOWER_E = 0x65;
const LOWER_Z = 0x7a;
const ORIGIN: Point = { x: 0, y: 0 };

/**
 * The path data of a figure — a rect, circle, ellipse, line, polyline or
 * polygon, by its attributes — or a path's own; undefined for anything else.
 */
export function figurePathData(
    tag: string,
    attribute: Attribute,
): string | undefined {
    if (tag === "polygon" || tag === "polyline") {
        const values = (attribute("points") ?? "").match(NUMBERS) ?? [];
        const corners: string[] = [];

        for (let index = 0; index + 1 < values.length; index += 2) {
            corners.push(
                `${n(Number(values[index]))} ${n(Number(values[index + 1]))}`,
            );
        }

        if (corners.length === 0) return;

        return `M${corners.join("L")}${tag === "polygon" ? "Z" : ""}`;
    }

    switch (tag) {
        case "circle": {
            const r = Math.abs(numberOf(attribute, "r"));

            return ellipsePathData(
                numberOf(attribute, "cx"),
                numberOf(attribute, "cy"),
                r,
                r,
            );
        }

        case "ellipse":
            return ellipsePathData(
                numberOf(attribute, "cx"),
                numberOf(attribute, "cy"),
                Math.abs(numberOf(attribute, "rx")),
                Math.abs(numberOf(attribute, "ry")),
            );

        case "line":
            return `M${n(numberOf(attribute, "x1"))} ${n(numberOf(attribute, "y1"))}L${n(numberOf(attribute, "x2"))} ${n(numberOf(attribute, "y2"))}`;

        case "path":
            return attribute("d") ?? undefined;

        case "rect":
            return rectPathData(attribute);

        default:
            return;
    }
}

/**
 * Path data read into its commands, each absolute: a relative command is
 * taken from where it starts, a horizontal or vertical line becomes a
 * line, a smooth curve gets the control point it mirrors, a move's further
 * pairs are lines. Undefined when the data cannot be read.
 */
export function parsePathData(data: string): PathCommand[] | undefined {
    const commands: PathCommand[] = [];
    const read = readPathData(data, (command) => {
        commands.push(command);

        return false;
    });

    return read ? commands : undefined;
}

/** The commands written as path data, coordinates to a thousandth. */
export function pathDataString(commands: readonly PathCommand[]): string {
    const parts: string[] = [];

    for (const command of commands) {
        switch (command.kind) {
            case "A":
                parts.push(
                    `A${n(command.rx)} ${n(command.ry)} ${n(command.rotation)} ${command.large ? "1" : "0"} ${command.sweep ? "1" : "0"} ${n(command.x)} ${n(command.y)}`,
                );

                break;

            case "C":
                parts.push(
                    `C${n(command.x1)} ${n(command.y1)} ${n(command.x2)} ${n(command.y2)} ${n(command.x)} ${n(command.y)}`,
                );

                break;

            case "Q":
                parts.push(
                    `Q${n(command.x1)} ${n(command.y1)} ${n(command.x)} ${n(command.y)}`,
                );

                break;

            case "Z":
                parts.push("Z");

                break;

            default:
                parts.push(`${command.kind}${n(command.x)} ${n(command.y)}`);
        }
    }

    return parts.join("");
}

/**
 * Reads path data command by command, each as `parsePathData` gives it, and
 * hands it to `visit` right away — whoever looks for something along a
 * path stops reading it where `visit` returns true, instead of reading
 * megabytes of a large path first. Whether the data could be read up to
 * there; false where it cannot, after the commands before.
 */
export function readPathData(
    data: string,
    visit: (command: PathCommand) => boolean | undefined,
): boolean {
    const reading: Reading = {
        control: undefined,
        done: false,
        point: ORIGIN,
        start: ORIGIN,
        visit,
    };
    let command = "";
    let position = pastSeparators(data, 0);

    while (position < data.length) {
        if (isLetter(data.codePointAt(position) ?? 0)) {
            command = data[position] ?? "";
            position = pastSeparators(data, position + 1);
        } else if (command === "" || command.toLowerCase() === "z") {
            // Numbers without a command, or after a close, are no path.
            return false;
        }

        const lower = command.toLowerCase();
        const arity = ARITY.get(lower);

        if (arity === undefined) return false;

        if (arity === 0) {
            emit(reading, { kind: "Z" });
            reading.point = reading.start;
            reading.control = undefined;

            if (reading.done) return true;

            continue;
        }

        const values: number[] = [];

        for (let index = 0; index < arity; index++) {
            // An arc's flags may stand without separators: "01" is two flags.
            const flag = lower === "a" && (index === 3 || index === 4);
            const end = flag
                ? flagEnd(data, position)
                : numberEnd(data, position);

            if (end === position) return false;

            values.push(Number(data.slice(position, end)));
            position = pastSeparators(data, end);
        }

        follow(reading, lower, command === lower, values);

        if (reading.done) return true;

        // Further pairs after a move are lines.
        if (lower === "m") command = command === "m" ? "l" : "L";
    }

    return true;
}

function cubic(first: Point, second: Point, to: Point): PathCommand {
    return {
        kind: "C",
        x: to.x,
        x1: first.x,
        x2: second.x,
        y: to.y,
        y1: first.y,
        y2: second.y,
    };
}

/** An ellipse as two half arcs, from its rightmost point round. */
function ellipsePathData(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
): string {
    const arc = `A${n(rx)} ${n(ry)} 0 0 1`;

    return `M${n(cx + rx)} ${n(cy)}${arc} ${n(cx - rx)} ${n(cy)}${arc} ${n(cx + rx)} ${n(cy)}Z`;
}

/** Hands `command` on to whoever reads; reading ends when they have what they wanted. */
function emit(reading: Reading, command: PathCommand) {
    if (reading.visit(command) === true) reading.done = true;
}

/** The end of an arc's flag at `from` in `data`: 0 or 1; `from` when there is none. */
function flagEnd(data: string, from: number): number {
    const code = data.codePointAt(from) ?? 0;

    return code === ZERO || code === ZERO + 1 ? from + 1 : from;
}

/**
 * Follows one command with its numbers: adds what it draws to the reading.
 * `relative` for a lower-case command.
 */
function follow(
    reading: Reading,
    command: string,
    relative: boolean,
    values: number[],
) {
    const [v0 = 0, v1 = 0, v2 = 0, v3 = 0, v4 = 0, v5 = 0, v6 = 0] = values;
    const origin = reading.point;

    function at(x: number, y: number): Point {
        return pointFrom(origin, relative, x, y);
    }

    if (command === "c" || command === "s") {
        const smooth = command === "s";
        const first = smooth ? mirrored(reading, "C", origin) : at(v0, v1);
        const second = smooth ? at(v0, v1) : at(v2, v3);
        const to = smooth ? at(v2, v3) : at(v4, v5);

        emit(reading, cubic(first, second, to));
        reading.point = to;
        reading.control = { family: "C", point: second };

        return;
    }

    if (command === "q" || command === "t") {
        const smooth = command === "t";
        const handle = smooth ? mirrored(reading, "Q", origin) : at(v0, v1);
        const to = smooth ? at(v0, v1) : at(v2, v3);

        emit(reading, quadratic(handle, to));
        reading.point = to;
        reading.control = { family: "Q", point: handle };

        return;
    }

    if (command === "a") {
        const to = at(v5, v6);

        emit(reading, {
            kind: "A",
            large: v3 !== 0,
            rotation: v2,
            rx: Math.abs(v0),
            ry: Math.abs(v1),
            sweep: v4 !== 0,
            x: to.x,
            y: to.y,
        });
        reading.point = to;
        reading.control = undefined;

        return;
    }

    let to: Point;

    if (command === "h") {
        to = { x: relative ? origin.x + v0 : v0, y: origin.y };
    } else if (command === "v") {
        to = { x: origin.x, y: relative ? origin.y + v0 : v0 };
    } else {
        to = at(v0, v1);
    }

    emit(reading, { kind: command === "m" ? "M" : "L", ...to });

    if (command === "m") reading.start = to;

    reading.point = to;
    reading.control = undefined;
}

function isDigit(code: number): boolean {
    return code >= ZERO && code <= NINE;
}

function isLetter(code: number): boolean {
    return (
        (code >= LOWER_A && code <= LOWER_Z) ||
        (code >= UPPER_A && code <= UPPER_Z)
    );
}

function isSeparator(code: number): boolean {
    // A space, tab, line feed, carriage return or comma.
    return (
        code === 0x20 ||
        code === 0x09 ||
        code === 0x0a ||
        code === 0x0d ||
        code === COMMA
    );
}

/**
 * The control point a smooth curve starts with: the last one mirrored at
 * `point`, when it was one of the same family; else `point` itself.
 */
function mirrored(
    reading: Reading,
    family: Control["family"],
    point: Point,
): Point {
    const { control } = reading;

    if (control?.family !== family) return point;

    return {
        x: 2 * point.x - control.point.x,
        y: 2 * point.y - control.point.y,
    };
}

function n(value: number): string {
    return String(Math.round(value * 1000) / 1000);
}

/**
 * The end of the number at `from` in `data` — a sign, digits with a point
 * among them, an exponent; `from` when there is none.
 */
function numberEnd(data: string, from: number): number {
    let position = from;
    let digits = 0;
    const sign = data.codePointAt(position) ?? 0;

    if (sign === PLUS || sign === MINUS) position++;

    while (isDigit(data.codePointAt(position) ?? 0)) {
        position++;
        digits++;
    }

    if ((data.codePointAt(position) ?? 0) === DOT) {
        position++;

        while (isDigit(data.codePointAt(position) ?? 0)) {
            position++;
            digits++;
        }
    }

    if (digits === 0) return from;

    const exponent = data.codePointAt(position) ?? 0;

    if (exponent === LOWER_E || exponent === UPPER_E) {
        let after = position + 1;
        const exponentSign = data.codePointAt(after) ?? 0;

        if (exponentSign === PLUS || exponentSign === MINUS) after++;

        if (isDigit(data.codePointAt(after) ?? 0)) {
            while (isDigit(data.codePointAt(after) ?? 0)) after++;

            position = after;
        }
    }

    return position;
}

/** The attribute as a number; 0 without one or for one that is none. */
function numberOf(attribute: Attribute, name: string): number {
    return Number.parseFloat(attribute(name) ?? "") || 0;
}

/** The position after the separators at `from`. */
function pastSeparators(data: string, from: number): number {
    let position = from;

    while (isSeparator(data.codePointAt(position) ?? 0)) position++;

    return position;
}

/** The point at (`x`, `y`): from `origin` on when `relative`, else as it is. */
function pointFrom(
    origin: Point,
    relative: boolean,
    x: number,
    y: number,
): Point {
    return relative ? { x: origin.x + x, y: origin.y + y } : { x, y };
}

function quadratic(handle: Point, to: Point): PathCommand {
    return { kind: "Q", x: to.x, x1: handle.x, y: to.y, y1: handle.y };
}

/** A corner radius as given, or undefined without one. */
function radiusOf(value: string | null | undefined): number | undefined {
    const radius = Number.parseFloat(value ?? "");

    return Number.isFinite(radius) ? Math.abs(radius) : undefined;
}

/** A rect's path, with rounded corners when it has them (a radius given once counts for both). */
function rectPathData(attribute: Attribute): string {
    const x = numberOf(attribute, "x");
    const y = numberOf(attribute, "y");
    const width = numberOf(attribute, "width");
    const height = numberOf(attribute, "height");
    const given = radiusOf(attribute("rx"));
    const givenY = radiusOf(attribute("ry"));
    const rx = Math.min(given ?? givenY ?? 0, width / 2);
    const ry = Math.min(givenY ?? given ?? 0, height / 2);

    if (rx === 0 || ry === 0) {
        return `M${n(x)} ${n(y)}L${n(x + width)} ${n(y)}L${n(x + width)} ${n(y + height)}L${n(x)} ${n(y + height)}Z`;
    }

    const arc = `A${n(rx)} ${n(ry)} 0 0 1`;

    return (
        `M${n(x + rx)} ${n(y)}L${n(x + width - rx)} ${n(y)}${arc} ${n(x + width)} ${n(y + ry)}` +
        `L${n(x + width)} ${n(y + height - ry)}${arc} ${n(x + width - rx)} ${n(y + height)}` +
        `L${n(x + rx)} ${n(y + height)}${arc} ${n(x)} ${n(y + height - ry)}` +
        `L${n(x)} ${n(y + ry)}${arc} ${n(x + rx)} ${n(y)}Z`
    );
}
